import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { SECURITY_FALLBACKS } from '../../../../../corelogic/domain/constants/displayFallbacks.constants';
import { TokenNotFoundError } from '../../../../../corelogic/domain/errors/authentication.error';
import { capitalizeFirstLetter } from '../../../../../corelogic/domain/utils/capitalize.utils';
import { CODE_TO_COUNTRY } from '../../../../../corelogic/domain/utils/country.utils';
import { SafeText } from '../../../../../corelogic/domain/utils/xssProtection.utils';
import { setAuthError } from '../../../../../corelogic/state/authentication/authentication.action';
import { store } from '../../../../../corelogic/state/store';
import {
  deleteUserProfile,
  setUserError,
} from '../../../../../corelogic/state/user/user.action';
import { useUserProfile } from '../../hooks/useUserProfile';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import { SuccessModal } from '../Shared/SuccessModal/SuccessModal';
import { ChangePassword } from './components/ChangePassword';
import { NationalityEditor } from './components/NationalityEditor';
import { ProfilePictureModal } from './components/ProfilePictureModal';
import { SettingsEditForm } from './components/SettingsEditForm';
import { UsernameEditModal } from './components/UsernameEditModal';

const styles = {
  container: {
    height: '100%',
    minHeight: '100vh',
    width: '100%',
    maxWidth: {
      xs: '100%',
      sm: '600px',
      md: '900px',
      lg: '1100px',
      xl: '1200px',
    },
    mx: 'auto',
    px: {
      xs: 2,
      sm: 3,
      md: 4,
    },
    pt: 4,
    pb: 10,
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), url('/viewer_octagon.png')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  title: {
    fontFamily: 'Rubik Mono One',
    color: '#ffffff',
    fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
    marginBottom: 4,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  section: {
    position: 'relative',
    p: { xs: 2, sm: 2.5, md: 3 },
    mb: 3,
    borderRadius: 2,
    background: 'rgba(20, 20, 20, 0.9)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
    transition: 'all 0.3s ease',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    overflow: 'hidden',
  },
  sectionTitle: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(1rem, 1.2vw, 1.2rem)',
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: 'white',
    position: 'relative',
    zIndex: 1,
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
  },
  profileAvatar: {
    width: 120,
    height: 120,
    border: '3px solid #8B0000',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
  },
  profileHeader: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'center', sm: 'flex-start' },
    gap: 3,
  },
  profileInfo: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 1,
  },
  username: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(1.2rem, 1.5vw, 1.5rem)',
    color: '#ffffff',
  },
  countryFlag: {
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '2px',
    overflow: 'hidden',
    height: '30px',
    width: '45px',
    display: 'inline-block',
    marginRight: 1,
  },
  countryName: {
    fontSize: 'clamp(0.875rem, 1vw, 1.1rem)',
    fontWeight: 400,
    marginLeft: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '30px',
  },
  editButton: {
    minWidth: 'auto',
    padding: '4px',
    color: 'rgba(255, 255, 255, 0.8)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    mt: 2,
  },
  redButton: {
    backgroundColor: '#8B0000',
    color: 'white',
    px: 3,
    py: 1,
    fontWeight: 500,
    width: '220px',
    height: '48px',
    gap: '5px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
    '&:hover': {
      backgroundColor: '#aa0000',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.5)',
      transform: 'translateY(1px)',
    },
  },
  divider: {
    my: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  field: {
    mb: 2,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 1,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    overflow: 'hidden',
    height: '75px',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
  },
  label: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 'clamp(0.85rem, 1vw, 0.85rem)',
    padding: '12px 16px 4px 16px',
    fontWeight: 400,
  },
  value: {
    color: 'white',
    fontSize: 'clamp(0.85rem, 1vw, 0.85rem)',
    fontWeight: 500,
    padding: '4px 16px 16px 16px',
    lineHeight: 1.2,
    flex: 1,
  },
  infoGrid: {
    mb: 3,
  },
  spacer: {
    height: '20px',
  },
};

interface SettingsProps {}

export const Settings: React.FC<SettingsProps> = () => {
  const { user, isLoading: isUserLoading } = useUserProfile();
  const token = store.getState().authentication.token;

  //State management
  const [isPasswordEditing, setIsPasswordEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  const handleDeleteProfile = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    try {
      if (!token) {
        store.dispatch(
          setAuthError(
            new TokenNotFoundError('Please sign in to delete your profile')
              .message,
          ),
        );
        return;
      }

      store.dispatch(
        deleteUserProfile({
          token,
          retry: 2,
        }),
      );

      setShowDeleteModal(false);
    } catch (error) {
      store.dispatch(setUserError('Failed to delete profile'));
      console.error('Failed to delete profile:', error);
    }
  };

  // When transitioning from edit mode of personal information
  const handleEditPersonalInfoSuccess = () => {
    setSuccessMessage(
      'Your profile information has been successfully updated.',
    );
    setShowSuccessModal(true);
    setIsEditing(false);
  };

  // Framer Motion Container
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // Framer Motion Item
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  return (
    <Container sx={styles.container}>
      {!user || !token ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh',
          }}
        >
          <Typography variant="h5" sx={{ color: 'white' }}>
            No user profile found, please sign in again...
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="h4" sx={styles.title}>
            Account Settings
          </Typography>

          {/*  Add motion container here to wrap all Paper sections */}
          <Box
            component={motion.div}
            variants={container}
            initial="hidden"
            animate="show"
          >
            {/* Profile Overview Section */}
            <Paper
              elevation={0}
              sx={styles.section}
              component={motion.div}
              variants={item}
            >
              <Box sx={styles.profileHeader}>
                <Box sx={{ position: 'relative', marginRight: 4 }}>
                  <Avatar
                    src={user?.profilePicture || ''}
                    alt={user?.username || 'User'}
                    sx={{
                      ...styles.profileAvatar,
                      border: '2px solid #8B0000',
                    }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      padding: '6px',
                      '&:hover': {
                        backgroundColor: '#8B0000',
                      },
                    }}
                    onClick={() => setShowProfilePictureModal(true)}
                  >
                    <CameraAltIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={styles.profileInfo}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={styles.username}>
                      <SafeText
                        text={user?.username}
                        fallback={SECURITY_FALLBACKS.USERNAME}
                      />
                    </Typography>
                    <Tooltip title="Edit Username">
                      <IconButton
                        size="small"
                        sx={{ ...styles.editButton, ml: 2 }}
                        onClick={() => setShowUsernameModal(true)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {user?.nationality && CODE_TO_COUNTRY[user.nationality] && (
                      <Box sx={styles.countryFlag}>
                        <ReactCountryFlag
                          countryCode={user.nationality}
                          svg
                          style={{
                            width: '100%',
                            height: '100%',
                            display: 'block',
                            objectFit: 'cover',
                          }}
                          title={CODE_TO_COUNTRY[user.nationality]}
                        />
                      </Box>
                    )}
                    <Typography
                      sx={{
                        ...styles.countryName,
                        marginLeft:
                          user?.nationality && CODE_TO_COUNTRY[user.nationality]
                            ? '10px'
                            : '0px',
                      }}
                    >
                      {CODE_TO_COUNTRY[user?.nationality || ''] ||
                        'Unknown Nationality'}
                    </Typography>
                    <NationalityEditor
                      user={user}
                      token={token}
                      isUserLoading={isUserLoading}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}
                  >
                    Member since:{' '}
                    {new Date(user?.createdAt || '').toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Username  Modal */}
            <UsernameEditModal
              isOpen={showUsernameModal}
              onClose={() => setShowUsernameModal(false)}
              currentUsername={user?.username || ''}
              userProfile={user}
              token={token}
            />

            {/* Profile Picture Modal */}
            <ProfilePictureModal
              isOpen={showProfilePictureModal}
              onClose={() => setShowProfilePictureModal(false)}
              token={token}
              currentPicture={user?.profilePicture || ''}
              userProfile={user}
            />

            {/* Personal Information Section */}
            <Paper
              elevation={0}
              sx={styles.section}
              component={motion.div}
              variants={item}
            >
              <Typography variant="h6" sx={styles.sectionTitle}>
                <PersonIcon /> Personal Information
              </Typography>
              {isEditing ? (
                <SettingsEditForm
                  user={user}
                  token={token}
                  isUserLoading={isUserLoading}
                  onCancel={() => setIsEditing(false)}
                  onSuccess={() => handleEditPersonalInfoSuccess()}
                />
              ) : (
                <Box>
                  <Grid container spacing={2} sx={styles.infoGrid}>
                    {/* Email - Full Width */}
                    <Grid item xs={12}>
                      <Box sx={styles.field}>
                        <Typography variant="body2" sx={styles.label}>
                          Email
                        </Typography>
                        <Typography sx={styles.value}>
                          <SafeText
                            text={user?.email}
                            fallback={SECURITY_FALLBACKS.EMAIL}
                          />
                        </Typography>
                      </Box>
                    </Grid>

                    {/* First Name and Last Name - Side by Side */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={styles.field}>
                        <Typography variant="body2" sx={styles.label}>
                          First Name
                        </Typography>
                        <Typography sx={styles.value}>
                          <SafeText
                            text={user?.firstName}
                            fallback={SECURITY_FALLBACKS.FIRST_NAME}
                          />
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={styles.field}>
                        <Typography variant="body2" sx={styles.label}>
                          Last Name
                        </Typography>
                        <Typography sx={styles.value}>
                          <SafeText
                            text={user?.lastName}
                            fallback={SECURITY_FALLBACKS.LAST_NAME}
                          />
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Gender and Date of Birth - Side by Side */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={styles.field}>
                        <Typography variant="body2" sx={styles.label}>
                          Gender
                        </Typography>
                        <Typography sx={styles.value}>
                          {capitalizeFirstLetter(
                            user?.gender || 'Not specified',
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={styles.field}>
                        <Typography variant="body2" sx={styles.label}>
                          Date of Birth
                        </Typography>
                        <Typography sx={styles.value}>
                          {user?.dateOfBirth
                            ? new Date(user?.dateOfBirth).toLocaleDateString()
                            : 'Not specified'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={styles.buttonContainer}>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      sx={styles.redButton}
                    >
                      Edit Information
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Security Section */}
            <Paper
              elevation={0}
              sx={styles.section}
              component={motion.div}
              variants={item}
            >
              <Typography variant="h6" sx={styles.sectionTitle}>
                <SecurityIcon /> Security
              </Typography>

              {!isPasswordEditing && (
                <Box sx={styles.field}>
                  <Typography variant="body2" sx={styles.label}>
                    Password
                  </Typography>
                  <Typography sx={styles.value}>••••••••</Typography>
                </Box>
              )}

              <ChangePassword
                token={token}
                isUserLoading={isUserLoading}
                onEditingChange={setIsPasswordEditing}
              />
            </Paper>

            {/* Account Management Section */}
            <Paper
              elevation={0}
              sx={styles.section}
              component={motion.div}
              variants={item}
            >
              <Typography variant="h6" sx={styles.sectionTitle} color="error">
                <DeleteForeverIcon /> Account Management
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}
              >
                Deleting your account will permanently remove all your data from
                our system. This action cannot be undone.
              </Typography>

              <Button
                variant="contained"
                startIcon={<DeleteForeverIcon />}
                onClick={handleDeleteProfile}
                sx={styles.redButton}
              >
                Delete Account
              </Button>
            </Paper>
            {/* Add bottom spacer */}
            <Box sx={styles.spacer}></Box>
          </Box>

          {/* Success Modal for Personal Information Edit */}
          <SuccessModal
            isOpen={showSuccessModal}
            onClose={() => setShowSuccessModal(false)}
            title="Profile Updated"
            message={successMessage}
            autoHideDuration={3000}
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title="Delete Profile"
            message="Are you sure you want to delete your profile? This action cannot be undone."
          />
        </>
      )}
    </Container>
  );
};
